import { Guid } from 'guid-typescript';
import { Observable } from 'rxjs';


export { TotalCostsService } from './total-costs/total-costs.service';
export { TotalCostsStateQuery } from './total-costs/total-costs.state';

export { CsBuildupStateStore, CsBuildupStateQuery } from './editable-section-part/editable-section-part.state';
export { EditableSectionPartComponent } from './editable-section-part/editable-section-part.component';

export interface OfferSection extends EntriesCollection {
  readonly id: Guid;
  readonly createdOn: Date;
}

export interface Product {
  readonly id: number;
  readonly name: string;
  readonly pricePerUnit: number;
  readonly version: number;
}

export interface EntriesCollection {
  readonly productRelatedEntries: ProductRelatedEntry[];
  readonly customEntries: CustomEntry[];
}

export interface EntyPriceInfo {
  readonly pricePerUnit: number;
  readonly discountFactor: number;
  readonly isAlternative: boolean;
}

export interface Entry extends EntyPriceInfo{
  readonly id: Guid;
  readonly pricePerUnit: number;
  readonly createdOn: Date;
  discountFactor: number;
  isAlternative: boolean;
  positionNumber: number;
}

export interface ProductRelatedEntry extends Entry {
  readonly productId: number;
  readonly productVersion: number;
  readonly description: string;
  readonly quantity: number;
}

export const MAX_CUSTOM_ENTRY_DESCRIPTION_LENGTH = 160;
export interface CustomEntry extends Entry {
  readonly description: string;
  readonly quantity: number;
}

export interface DataInfo<T> {
  data: T;
  isNew: boolean;
}

export function getEntryTotalCosts(
  entryPriceInfo: EntyPriceInfo,
  quantity: number): number {
  return entryPriceInfo.isAlternative
    ? 0
    : entryPriceInfo.pricePerUnit * quantity * (1 - entryPriceInfo.discountFactor);
}

export function createProduct(entry: ({productId: number, description: string, pricePerUnit: number, productVersion: number})): Product {
  return {
    id: entry.productId,
    name: entry.description,
    pricePerUnit: entry.pricePerUnit,
    version: entry.productVersion
  };
}

export function saveChanges<T>(projectId: Guid, data: T, dataInfo: DataInfo<T>,
                               addFunc: (projectId: Guid, data: T) => Observable<T>,
                               updateFunc: (projectId: Guid, data: T) => Observable<T>): void {
  if (dataInfo.isNew) {
    addFunc(projectId, data)
      .subscribe(updated => {
        dataInfo.isNew = false;
        dataInfo.data = updated;
      });
  }
  else {
    updateFunc(projectId, data)
      .subscribe(updated => {
        dataInfo.data = updated;
      });
  }
}

export function discardChanges<T>(dataInfo: DataInfo<T>): void {
  if (dataInfo.isNew) {
    dataInfo.data = undefined;
    dataInfo.isNew = false;
  }
}

export function remove<T>(projectId: Guid, dataInfo: DataInfo<T>,
                          removeFunc: (projectId: Guid) => Observable<T>): void {
  if (!dataInfo.isNew) {
    removeFunc(projectId)
      .subscribe(() => {
        dataInfo.data = undefined;
        dataInfo.isNew = false;
      });
  }
  else {
    dataInfo.data = undefined;
  }
}

declare global {
  interface Array<T> {
    leftJoin<TInner, TResult>(
      inner: ReadonlyArray<TInner>,
      outerKeySelector: (outer: T) => any,
      innerKeySelector: (inner: TInner) => any,
      resultSelector: (outer: T, inner: TInner) => TResult): TResult[];
  }
}

Array.prototype.leftJoin = function <T, TInner, TResult>(
  this: ReadonlyArray<T>,
  inner: ReadonlyArray<TInner>,
  outerKeySelector: (outer: T) => any,
  innerKeySelector: (inner: TInner) => any,
  resultSelector: (outer: T, inner: TInner) => TResult): TResult[] {
    return this.map(
      outerItem => resultSelector(
        outerItem,
        inner.find(innerItem => outerKeySelector(outerItem) === innerKeySelector(innerItem)))
    );
  };
